import React from 'react';
import styled from 'styled-components';
import { RoutineAssignment } from '../types/alarm';

interface RoutineAssignmentBlockProps {
  routine: RoutineAssignment;
  onDelete: (routine: RoutineAssignment) => void;
  onEdit: (routine: RoutineAssignment) => void;
  onCheck: (routine: RoutineAssignment) => void;
  onSettings?: () => void; // 환경설정 함수 추가
  onCopy: (routine: RoutineAssignment) => void; // 복사 함수 추가
  onMenuToggle: (routineId: string) => void; // 햄버거 메뉴 토글 함수 추가
  isMenuOpen: boolean; // 메뉴 열림 상태 추가
  categoryName?: string; // 카테고리 이름 추가
  categoryColor?: string; // 카테고리 색상 추가
  members: any[];
  currentClassId: string;
}

const RoutineAssignmentBlock: React.FC<RoutineAssignmentBlockProps> = ({ 
  routine, 
  onDelete: _onDelete, 
  onEdit: _onEdit, 
  onCheck, 
  onSettings: _onSettings, 
  onCopy: _onCopy,
  onMenuToggle,
  isMenuOpen: _isMenuOpen,
  categoryName: _categoryName,
  categoryColor: _categoryColor,
  members: _members, 
  currentClassId: _currentClassId 
}) => {
  const rawDue = routine.dueDate;
  const dueDate = rawDue?.toDate ? rawDue.toDate() : new Date(rawDue);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const isOverdue = due < today;

  const routineDaysText = routine.routineDays
    .map(day => ['일', '월', '화', '수', '목', '금', '토'][day])
    .join(', ');

  return (
    <BlockContainer 
      onClick={() => onCheck(routine)}
      data-routine-id={routine.id}
      $isOverdue={isOverdue}
    >
      <TopRow>
        <Left>
          <RoutineBadge $isOverdue={isOverdue}>루틴</RoutineBadge>
        </Left>
        <TitleCenter $isOverdue={isOverdue}>{routine.title}</TitleCenter>
        <Right>
          <HamburgerButton
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle(routine.id);
            }}
            aria-label="메뉴"
            className="hamburger-button"
            $isOverdue={isOverdue}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="1" fill="currentColor"/>
              <circle cx="19" cy="12" r="1" fill="currentColor"/>
              <circle cx="5" cy="12" r="1" fill="currentColor"/>
            </svg>
          </HamburgerButton>
        </Right>
      </TopRow>
      
      <DueDate $isOverdue={isOverdue}>
        <DueDateText>
          {`${dueDate.getMonth()+1}/${dueDate.getDate()}(${['일', '월', '화', '수', '목', '금', '토'][dueDate.getDay()]}) • 기본포인트 ${routine.basePoints}`}
          {(routine.rewardEnabled && routine.rewardCoins !== undefined && routine.rewardCoins >= 1) ||
           (routine.rewardItemCardIds && routine.rewardItemCardIds.length > 0) ? (
            <RewardInfo>
              {routine.rewardEnabled && routine.rewardCoins !== undefined && routine.rewardCoins >= 1 && (
                <RewardItem>
                  <CoinIcon>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                      <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                      <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                    </svg>
                  </CoinIcon>
                  <RewardText>{routine.rewardCoins}</RewardText>
                </RewardItem>
              )}
              {routine.rewardItemCardIds && routine.rewardItemCardIds.length > 0 && (
                <RewardItem>
                  <ItemCardIcon>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                      <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                    </svg>
                  </ItemCardIcon>
                  <RewardText>{routine.rewardItemCardIds.length}</RewardText>
                </RewardItem>
              )}
            </RewardInfo>
          ) : null}
        </DueDateText>
      </DueDate>
      
      <RoutineDays $isOverdue={isOverdue}>{routineDaysText}</RoutineDays>
    </BlockContainer>
  );
};

const BlockContainer = styled.div<{ $isOverdue?: boolean }>`
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 6px 10px;
  margin: 1px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  opacity: ${p => (p.$isOverdue ? 0.7 : 1)};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => (p.$isOverdue ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 8px 24px rgba(0, 0, 0, 0.15)')};
  }
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.3);
  }
  
  &:focus-visible {
    outline: none;
    border-color: #000;
    box-shadow: none;
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 40px;
  align-items: center;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const TitleCenter = styled.div<{ $isOverdue?: boolean }>`
  text-align: center;
  font-weight: 800;
  font-size: 1.2rem;
  color: ${p => (p.$isOverdue ? '#b0b0b0' : 'var(--color-text-primary)')};
  flex: 1;
  opacity: ${p => (p.$isOverdue ? 0.7 : 1)};
`;

const Right = styled.div`
  display: flex;
  gap: 8px;
  min-width: 0;
  justify-content: flex-end;
`;

const RoutineBadge = styled.span<{ $isOverdue?: boolean }>`
  background: #4f46e5;
  color: white;
  padding: 2px 5px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  opacity: ${p => (p.$isOverdue ? 0.3 : 1)};
`;

const HamburgerButton = styled.button<{ $isOverdue?: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${p => (p.$isOverdue ? '#b0b0b0' : 'var(--color-text-gray)')};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => (p.$isOverdue ? 0.6 : 1)};

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const RoutineDays = styled.div<{ $isOverdue?: boolean }>`
  text-align: center;
  color: ${p => (p.$isOverdue ? '#b0b0b0' : '#4f46e5')};
  margin-top: 2px;
  opacity: ${p => (p.$isOverdue ? 0.6 : 1)};
`;

const DueDate = styled.div<{ $isOverdue?: boolean }>`
  text-align: center;
  color: ${p => (p.$isOverdue ? '#b0b0b0' : 'var(--color-text-gray)')};
  margin-top: 1px;
  opacity: ${p => (p.$isOverdue ? 0.6 : 1)};
`;

const DueDateText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RewardInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const CoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const ItemCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const RewardText = styled.span`
  font-size: inherit;
  font-weight: 600;
  color: inherit;
`;


export default RoutineAssignmentBlock; 